import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import {
  CopyProps,
  UtilityCopy,
  UtilityCopyProps,
} from "@bluebottlecoffee/design-system";
import { BaseProps, filters } from "../../../lib/sanity/shared";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../lib/utils";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import { TermsOfUsePage as TermsOfUseSchema } from "../../../lib/sanity-schema";
import sanityClient from "../../../lib/sanity-client";
import {
  buildTranslationGroupQuery,
  toCopyObject,
  TranslationGroupSanityType,
} from "../../../lib/sanity/translation-group-queries";
import { toUtilityCopyProps } from "../../../lib/transformers/utility-copy";

interface PageData extends BaseProps {
  termsOfUsePage: TermsOfUseSchema;
  utilityTranslations: CopyProps;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);

  const { lang } = defaultProps;

  const query = buildTranslationGroupQuery(lang, "UtilityPage");

  const utilityTranslations = await sanityClient()
    .fetch<TranslationGroupSanityType>(query)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<CopyProps>(lang, translations),
    );

  const termsOfUsePage = await sanityClient().fetch<TermsOfUseSchema>(`*[
    ${filters.whereType("termsOfUsePage")} &&
    ${filters.excludeDrafts}
  ][0]`);

  return {
    props: {
      termsOfUsePage,
      utilityTranslations,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: TermsOfUseSchema) {
  if (!data) {
    throw Error("Page data not found, please ensure one has been created.");
  }
  if (!data.title) {
    throw Error("Privacy Page must have a title.");
  }
}

const TermsOfUsePage: FunctionComponent<PageData> = ({
  termsOfUsePage,
  utilityTranslations,
  ...layoutWrapperProps
}) => {
  validate(termsOfUsePage);

  const { region, lang } = layoutWrapperProps;

  const utilityCopyProps: UtilityCopyProps = {
    ...toUtilityCopyProps(termsOfUsePage.copy, region, lang),
    copyProps: utilityTranslations,
  };

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        pageTitle: termsOfUsePage.title[lang],
        metaTags: termsOfUsePage.metaTags,
      }}
    >
      {termsOfUsePage.copy && <UtilityCopy {...utilityCopyProps} />}
    </LayoutWrapper>
  );
};

export default TermsOfUsePage;
